// src/components/leitner/TestTakerView.jsx
import React, { useState, useEffect } from 'react';
import { MathRenderer } from '../math/MathRenderer';

const TestTakerView = ({ test, questions, onSubmitTest, onBackToList }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState(test.timeLimitPerQuestion || 0);

  const currentQuestion = questions[currentIndex];

  useEffect(() => {
    if (test.timeLimitPerQuestion > 0 && !showResults) {
      setTimeLeft(test.timeLimitPerQuestion);
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmitAnswer();
            return test.timeLimitPerQuestion;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentIndex, showResults]);

  const handleOptionToggle = (optionId) => {
    setSelectedOptionIds(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleSubmitAnswer = () => {
    const correct =
      selectedOptionIds.length === currentQuestion.correctOptionIds.length &&
      selectedOptionIds.every(id => currentQuestion.correctOptionIds.includes(id));

    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: {
        selectedOptionIds,
        correct
      }
    }));

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOptionIds([]);
    } else {
      const totalCorrect = Object.values({ ...answers, [currentQuestion.id]: { correct } })
        .filter(a => a.correct).length;
      onSubmitTest(totalCorrect);
      setShowResults(true);
    }
  };

  if (showResults) {
    const score = Object.values(answers).filter(a => a.correct).length;
    const percentage = Math.round((score / questions.length) * 100);

    let outcomeMessage = '';
    if (test.passingScore && percentage >= test.passingScore) {
      outcomeMessage = test.passMessage || 'Congratulations, you passed!';
    } else if (test.passingScore && percentage < test.passingScore) {
      outcomeMessage = test.failMessage || 'Keep studying, you can do it!';
    }

    return (
      <div className="text-center bg-slate-800 p-8 rounded-lg shadow-xl">
        <h2 className="text-3xl font-bold text-green-400">Test Complete!</h2>
        <p className="text-slate-300 my-2">You have completed the "{test.name}" test.</p>
        <p className="text-5xl font-bold my-4">
          {score} / {questions.length} <span className="text-3xl text-slate-400">({percentage}%)</span>
        </p>
        {outcomeMessage && (
          <p className={`text-lg font-semibold mb-6 ${
            percentage >= test.passingScore ? 'text-green-400' : 'text-amber-400'
          }`}>
            {outcomeMessage}
          </p>
        )}
        <button
          onClick={onBackToList}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-md transition"
        >
          Back to Test Center
        </button>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="text-center bg-slate-800 p-8 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold text-slate-300">Loading Test...</h2>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-800 p-6 sm:p-8 rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-400">
          Question {currentIndex + 1} of {questions.length}
        </div>
        {test.timeLimitPerQuestion > 0 && (
          <div className={`text-lg font-bold px-3 py-1 rounded-md ${
            timeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-slate-300'
          }`}>
            Time: {timeLeft}s
          </div>
        )}
      </div>
      <div className="w-full max-w-2xl mx-auto">
        <h3 className="text-xl sm:text-2xl font-bold mb-6 text-center text-slate-200">
          <MathRenderer content={currentQuestion.text} enableMarkdown={true} enableMath={true} />
        </h3>
        <div className="space-y-3 mb-8">
          {currentQuestion.options.map((option) => (
            <label
              key={option.id}
              className="flex items-center p-4 rounded-md border border-slate-600 bg-slate-700 hover:bg-slate-600 transition-all duration-200 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedOptionIds.includes(option.id)}
                onChange={() => handleOptionToggle(option.id)}
                className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-indigo-500 focus:ring-indigo-600 mr-4"
              />
              <span className="text-slate-200 flex-grow">
                <MathRenderer content={option.text} enableMarkdown={false} enableMath={true} />
              </span>
            </label>
          ))}
        </div>
        <button
          onClick={handleSubmitAnswer}
          disabled={selectedOptionIds.length === 0}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition disabled:bg-slate-600 disabled:cursor-not-allowed"
        >
          {currentIndex === questions.length - 1 ? 'Submit & Finish Test' : 'Submit & Next →'}
        </button>
      </div>
    </div>
  );
};

export default TestTakerView;